import { Container, Grid, makeStyles, Typography } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import React from 'react';

type SectionProps = {
  variant?: 'hero-home' | 'hero-green' | 'hero-teal' | 'grey' | 'default';
  verticalSpacing?: number;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  className?: string;
  topCurve?: boolean;
};

const useStyles = makeStyles(theme => ({
  section: (props: SectionProps) => {
    switch (props.variant) {
      case 'hero-home':
        return {
          background:
            'linear-gradient(226.38deg, rgba(0, 155, 141, 0.1566) 11.89%, rgba(0, 192, 109, 0.1102) 100%)',
          [theme.breakpoints.up('md')]: {
            marginBottom: -theme.spacing(16),
            marginTop: props.topCurve ? -theme.spacing(16) : 0,
          },
        };
      case 'hero-green':
        return {
          background:
            'linear-gradient(180deg, rgba(0, 190, 111, 0.2) 0%, rgba(153, 229, 197, 0.2) 50%, rgba(255, 255, 255, 0.2) 100%)',
          textAlign: 'center',
          [theme.breakpoints.up('md')]: {
            marginTop: props.topCurve ? -theme.spacing(16) : 0,
          },
        };
      case 'hero-teal':
        return {
          background:
            'linear-gradient(180deg, rgba(0, 155, 141, 0.2) 0%, rgba(103, 191, 183, 0.2) 50%, rgba(255, 255, 255, 0.2) 100%)',
          textAlign: 'center',
          [theme.breakpoints.up('md')]: {
            marginTop: props.topCurve ? -theme.spacing(16) : 0,
          },
        };
      case 'grey':
        return {
          backgroundColor: grey[100],
          [theme.breakpoints.up('md')]: {
            marginTop: props.topCurve ? -theme.spacing(16) : 0,
          },
        };
    }
  },

  container: (props: SectionProps) => ({
    paddingTop: theme.spacing(Number.isInteger(props.verticalSpacing) ? props.verticalSpacing : 12),
    paddingBottom: theme.spacing(
      Number.isInteger(props.verticalSpacing) ? props.verticalSpacing : 12,
    ),
  }),

  curve: (props: SectionProps) => ({
    [theme.breakpoints.up('md')]: {
      display: props.variant === 'hero-home' ? 'block' : 'none',
      background: theme.palette.background.default,
      height: theme.spacing(16),
      borderTopRightRadius: '50%',
      borderTopLeftRadius: '50%',
    },
  }),

  topCurve: (props: SectionProps) => ({
    [theme.breakpoints.up('md')]: {
      display: props.topCurve ? 'block' : 'none',
      background: theme.palette.background.default,
      height: theme.spacing(16),
      borderBottomRightRadius: '50%',
      borderBottomLeftRadius: '50%',
    },
  }),
}));

const useTitleStyles = makeStyles(theme => ({
  titleWrapper: {
    marginBottom: theme.spacing(6),
  },

  title: {
    color: theme.palette.primary.main,
    fontSize: '2rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },

  subtitle: {
    marginBottom: theme.spacing(2),
  },
}));

export const SectionTitle = (props: { title: string; subtitle?: string }) => {
  const classes = useTitleStyles();
  return (
    <Grid container justifyContent="center" className={classes.titleWrapper}>
      <Grid item lg={8} md={10}>
        <Typography variant="h3" align="center" component="div" className={classes.title}>
          {props.title}
        </Typography>
        {props.subtitle && (
          <Typography variant="subtitle2" align="center" className={classes.subtitle}>
            {props.subtitle}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

const Section: React.FC<SectionProps> = props => {
  const classes = useStyles(props);

  return (
    <div className={`${classes.section} ${props.className}`}>
      <div className={classes.topCurve} />

      <Container className={classes.container} maxWidth={props.maxWidth}>
        {props.children}
      </Container>

      <div className={classes.curve} />
    </div>
  );
};

Section.defaultProps = {
  variant: 'default',
  className: '',
};

export default Section;
